import { Collection } from 'models/Collection';
import {
  createEmptyTask,
  DocMemo,
  Memo,
  MemoType,
  getParsedDocument,
  getParsedTasks,
  LinkMemo,
  QuoteMemo,
  TaskListMemo,
  TextMemo,
  CalendarMemo,
  ImageMemo,
  AudioMemo,
  VideoMemo,
  FileMemo,
  EMPTY_TIPTAP_DOCUMENT,
  hasCustomDate,
} from 'models/Memo';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { JSONContent } from '@tiptap/react';
import { useDraft } from 'xstate/hooks/useDraft';
import { Draft } from 'xstate/machines/DraftMachine';
import {
  ComposerDraftFields,
  getComposerDraft,
} from 'xstate/actors/ComposerDraftActor';
import { utcToLocalDate } from 'utils/DateTimeUtils';
import type { Media } from 'models/Media';
import type { UploadItem } from './useMediaUpload';
import { useTranslations } from 'next-intl';

// Seeds a media field with already-uploaded items, so editing a memo shows
// its existing attachments without a separate fetch/sync step.
const itemsFromMedia = (media?: Media[]): UploadItem[] =>
  (media ?? []).map((m) => ({
    key: m.id,
    previewUrl: m.href,
    status: 'done' as const,
    mediaId: m.id,
    isExisting: true,
  }));

interface Props {
  defaultCollection?: Collection;
  memo?: Memo;
  boxId?: string;
}

export interface FormValues {
  // Universal fields
  id?: string; // Optional as is `undefined` if it's a create, not an update
  type: MemoType;
  collectionId?: string | null;
  content?: string;
  name?: string;
  // Dynamic Fields
  author?: string | null;
  isTimeSet?: boolean;
  at?: Date | null;
  tasks?: string[];
  document?: JSONContent;
  imageIds?: UploadItem[];
  audioIds?: UploadItem[];
  videoIds?: UploadItem[];
  fileIds?: UploadItem[];
  // Optional Fields
  isAllDay?: boolean;
  isMoment?: boolean;
  startAt?: Date;
  endAt?: Date;
  // The memo's own date, behind the Date chip. Null means none picked.
  datedAt?: Date | null;
  title?: string;
  description?: string;
  // Holds just the source content; the `{ type, content }` object is assembled
  // in the transformer (backend classifies URL vs text from `type: 'auto'`).
  source?: string;
}

type FormKeys = keyof FormValues;

export function useFormInitialisation({
  defaultCollection,
  memo,
  boxId,
}: Props) {
  const t = useTranslations();
  const { currentDraft } = useDraft();
  // Never restore into an edit — that would clobber the memo being edited.
  const composerDraft = !memo ? getComposerDraft(boxId) : null;

  const methods = useForm<FormValues>({
    defaultValues: defaultFormValues(
      memo,
      defaultCollection,
      currentDraft,
      composerDraft,
    ),
    mode: 'onChange',
  });

  const { register, unregister, setValue, watch } = methods;

  const selectedMemoType = watch('type');

  useEffect(() => {
    (
      ['id', 'type', 'collectionId', 'content', 'datedAt'] as FormKeys[]
    ).forEach((field) => register(field));

    // Registered once — each validator checks its own type, so there's
    // nothing to unregister when the type changes.
    const hasAnUploadedItem = (value?: UploadItem[]) =>
      Array.isArray(value) &&
      value.some((item) => item.status === 'done' && item.mediaId);

    register('imageIds', {
      validate: (value, formValues) =>
        formValues.type !== MemoType.Image ||
        hasAnUploadedItem(value) ||
        t('memoComposer.image.required'),
    });
    register('audioIds', {
      validate: (value, formValues) =>
        formValues.type !== MemoType.Audio ||
        hasAnUploadedItem(value) ||
        t('memoComposer.audio.required'),
    });
    register('videoIds', {
      validate: (value, formValues) =>
        formValues.type !== MemoType.Video ||
        hasAnUploadedItem(value) ||
        t('memoComposer.video.required'),
    });
    register('fileIds', {
      validate: (value, formValues) =>
        formValues.type !== MemoType.File ||
        hasAnUploadedItem(value) ||
        t('memoComposer.file.required'),
    });

    switch (selectedMemoType) {
      case MemoType.Text:
      case MemoType.Link:
      case MemoType.Image:
      case MemoType.Audio:
      case MemoType.Video:
      case MemoType.File:
        unregister('author');
        break;
      case MemoType.Doc:
      case MemoType.TaskList:
      case MemoType.Calendar:
        unregister('content');
        unregister('author');
        break;
    }
  }, [register, unregister, selectedMemoType]);

  // `useForm`'s `defaultValues` only applies on the first render, so
  // `collectionId` needs its own effect to track later changes to
  // `defaultCollection`/`memo`. Only acts when the *target* itself changes
  // from what it was last time this ran — not just "not the first run" —
  // because `defaultCollection` can re-render with a new reference (e.g. an
  // unrelated update on the shared space actor) without its `id` actually
  // changing, and a naive "skip only render #1" guard would treat that as a
  // real change and clobber a restored composer draft's `collectionId`.
  const hasSyncedCollectionOnce = useRef(false);
  const lastSyncedCollectionTarget = useRef<string | null>(null);
  useEffect(() => {
    // Editing an existing memo must never have its collection reassigned by
    // the sidebar's ambient selection — only the memo's own collection
    // counts while editing.
    const target = memo
      ? (memo.collectionId ?? null)
      : (defaultCollection?.id ?? null);

    if (!hasSyncedCollectionOnce.current) {
      hasSyncedCollectionOnce.current = true;
      lastSyncedCollectionTarget.current = target;
      return;
    }

    if (target === lastSyncedCollectionTarget.current) return;

    lastSyncedCollectionTarget.current = target;
    setValue('collectionId', target);
  }, [defaultCollection, memo, setValue]);

  //
  // DEBUG: Logger: Log registered fields
  //
  // useEffect(() => {
  //   console.log('Selected memo type:', selectedMemoType);

  //   const logRegisteredFields = () => {
  //     console.log('Currently registered fields:', Object.keys(control._fields));
  //   };

  //   logRegisteredFields();
  // }, [control, selectedMemoType]);

  return methods;
}

export function defaultFormValues(
  memo?: Memo,
  defaultCollection?: Collection | null,
  currentDraft?: Draft | null,
  composerDraft?: ComposerDraftFields | null,
) {
  // Start with default fields that are common across all memo types
  const defaults: { [key: string]: any } = {
    // universal fields
    type: MemoType.Text,
    collectionId: defaultCollection?.id || null,
    // dynamic fields
    content: '',
    name: '',
    document: currentDraft?.document || EMPTY_TIPTAP_DOCUMENT,
    author: '',
    at: new Date(),
    isTimeSet: false,
    datedAt: null,
    tasks: [createEmptyTask()],
    imageIds: [],
    audioIds: [],
    videoIds: [],
    fileIds: [],
    // optional fields
    title: '',
    description: '',
    source: '',
  };

  // Safely handle potentially null 'memo'
  if (!memo) {
    // The composer draft wins over the plain empty defaults above.
    return { ...defaults, ...composerDraft };
  }

  // Generic memo fields
  defaults.id = memo.id;
  defaults.type = memo.type;
  defaults.collectionId = memo.collectionId ?? null;
  // Only a date the memo was given shows in the field; the creation time
  // stays implicit, as on Mac.
  defaults.datedAt = hasCustomDate(memo) ? new Date(memo.datedAt) : null;

  // Handle type-specific default values and conditions
  switch (memo.type) {
    case MemoType.Text: {
      const textMemo = memo as TextMemo;
      defaults.content = textMemo.content;
      defaults.title = textMemo.title;
      defaults.source = textMemo.source?.content ?? '';
      break;
    }
    case MemoType.Link: {
      const linkMemo = memo as LinkMemo;
      defaults.content = linkMemo.content;
      defaults.title = linkMemo.title;
      defaults.description = linkMemo.description ?? '';
      break;
    }
    case MemoType.Quote: {
      const quoteMemo = memo as QuoteMemo;
      defaults.author = quoteMemo.author;
      defaults.content = quoteMemo.content;
      defaults.title = quoteMemo.title;
      defaults.description = quoteMemo.description ?? '';
      defaults.source = quoteMemo.source?.content ?? '';
      break;
    }
    case MemoType.Doc: {
      const pageMemo = memo as DocMemo;
      defaults.document = getParsedDocument<DocMemo>(pageMemo);
      defaults.title = pageMemo.title;
      break;
    }
    case MemoType.TaskList: {
      const taskListMemo = memo as TaskListMemo;
      defaults.tasks = getParsedTasks(taskListMemo);
      defaults.title = taskListMemo?.title || '';
      defaults.description = taskListMemo.description ?? '';
      break;
    }
    case MemoType.Image: {
      const imageMemo = memo as ImageMemo;
      defaults.imageIds = itemsFromMedia(imageMemo.images);
      defaults.title = imageMemo.title ?? '';
      // The caption is edited via the shared `content` field.
      defaults.content = imageMemo.description ?? '';
      break;
    }
    case MemoType.Audio: {
      const audioMemo = memo as AudioMemo;
      defaults.audioIds = itemsFromMedia(audioMemo.audios);
      defaults.title = audioMemo.title ?? '';
      defaults.content = audioMemo.description ?? '';
      break;
    }
    case MemoType.Video: {
      const videoMemo = memo as VideoMemo;
      defaults.videoIds = itemsFromMedia(videoMemo.videos);
      defaults.title = videoMemo.title ?? '';
      defaults.content = videoMemo.description ?? '';
      break;
    }
    case MemoType.File: {
      const fileMemo = memo as FileMemo;
      defaults.fileIds = itemsFromMedia(fileMemo.files);
      defaults.title = fileMemo.title ?? '';
      defaults.content = fileMemo.description ?? '';
      break;
    }
    case MemoType.Calendar: {
      const { name, description, isAllDay, isMoment, start, end } =
        memo as CalendarMemo;

      defaults.name = name ?? '';
      defaults.description = description ?? '';

      defaults.isAllDay = Boolean(isAllDay);
      defaults.isMoment = Boolean(isMoment);

      const startDate = utcToLocalDate(start?.dateTime);
      const endDate = utcToLocalDate(end?.dateTime);

      if (startDate) {
        defaults.startAt = new Date(startDate);
        defaults.endAt = new Date(endDate ?? startDate);
      }

      break;
    }
  }

  return defaults;
}
